import React, { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useParams } from 'react-router'
import { GetFormat, PostFormat, api, getProvider, updateProvider, useGet } from 'sharedKernel/api'
import { Loading } from 'components/loading'
import { PageDescription, PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { showDoubleActionPopUp } from 'components/popup/doubleAction'
import { trans } from 'sharedKernel/i18n'
import { SOLUTION_STATUS } from 'sharedKernel/constants'
import { HubCompanyChallengeSolutionsTable } from './table'
import { SpecialComplianceTable } from './specialComplianceTable'
import { showToast } from 'components/toast'
import { triggerFileDownloadOnClientSide } from 'sharedKernel/file/handler'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ExportDropdown } from './export/ExportDropdown'
import { BaseButton } from 'iebt-zing-storybook'
import { isBooleanEvaluationStep } from './formatBooleanEvaluationValue'

const SWIPER_TABS = {
  SUBSCRIPTIONS: 0,
  APPROVED: 1,
  DISAPPROVED: 2
}

export const HubCompanyChallengeSolutionsManagement = (): JSX.Element => {
  const { challengeId } = useParams()

  const { mutate: approveSolution } = updateProvider()
  const { mutate: archiveSolution } = updateProvider()
  const { mutate: downloadFile } = getProvider()
  const { mutate: fetchSolutions } = getProvider()

  const [activeSection] = useState(0)
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  const [tabValue, setTabValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStage, setSelectedStage] = useState('')

  const [stepsData, setStepsData] = useState<any[]>([])
  const [solutionsData, setSolutionsData] = useState<any>({ rows: [] })
  const [isSolutionsFetching, setIsSolutionsFetching] = useState(false)
  const [selectedStepData, setSelectedStepData] = useState<any>(null)
  const [evaluationsSummaryData, setEvaluationsSummaryData] = useState<any>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const stepsRequest: GetFormat = {
    url: `/v1/hub/company/challenge/${challengeId}/steps`
  }

  const { data: stepsResponse } = useGet<any>(
    stepsRequest,
    `challenge-steps-get-${challengeId}`
  )

  const labels = {
    pageTitle: trans('Gerenciar inscrições'),
    description: trans(''),
    subscriptions: trans('Inscrições'),
    approve: trans('Aprovado'),
    archive: trans('Reprovado'),
    approveConfirmationModal: trans('Tem certeza que deseja aprovar essa solução?'),
    approveConfirmationModalTitle: trans('Aprovar solução'),
    archiveConfirmationModal: trans('Tem certeza que deseja reprovar essa solução?'),
    archiveConfirmationModalTitle: trans('Reprovar solução'),
    successApprove: trans('Solução aprovada com sucesso!'),
    successArchive: trans('Solução reprovada com sucesso!'),
    exportEvaluationData: trans('Exportar dados de avaliação'),
    exportSolutionsData: trans('Exportar modelo de importação'),
    exportSolutionData: trans('Exportar dados de inscrição'),
    exportSolutionsPdf: trans('Exportar PDF')
  }

  const notifyExport = () => showToast('info', 'Seu arquivo será baixado em breve, por favor aguarde')

  const stageOptions = React.useMemo(() => {
    if (stepsData && stepsData.length > 0) {
      return stepsData.map((step: any) => ({
        value: step.id || step._id,
        label: step.name || step.title
      }))
    }

    return []
  }, [stepsData])

  useEffect(() => {
    if (stepsResponse) {
      const steps = stepsResponse?.data?.steps || stepsResponse?.data || stepsResponse

      if (Array.isArray(steps)) {
        setStepsData(steps)

        if (steps.length >= 1) {
          const etapa1 = steps[0]
          setSelectedStage(etapa1.id || etapa1._id)
        }
      }
    }
  }, [stepsResponse, challengeId])

  useEffect(() => {
    switch (activeSection) {
      case SWIPER_TABS.SUBSCRIPTIONS:
        changeTabActions('')
        break
      case SWIPER_TABS.APPROVED:
        changeTabActions(SOLUTION_STATUS.APPROVED)
        break
      case SWIPER_TABS.DISAPPROVED:
        changeTabActions(SOLUTION_STATUS.DISAPPROVED)
        break
    }
  }, [activeSection])

  useEffect(() => {
  if (!selectedStage || stepsData.length === 0) return

  const stepData = stepsData.find(
    (step: any) => (step.id || step._id) === selectedStage
  )

  if (!stepData) return

  setSelectedStepData(stepData)
  setIsSolutionsFetching(true)

  const request: GetFormat = {
    url: `/v1/hub/idea-evaluation-step/step/${selectedStage}/summary`
  }

  fetchSolutions(request, {
    onSuccess: (data) => {
      const actualData = data?.data || data

      if (actualData?.rows) setSolutionsData(actualData)
      else if (Array.isArray(actualData)) setSolutionsData({ rows: actualData })
      else setSolutionsData({ rows: [] })

      setEvaluationsSummaryData(actualData)
      setIsSolutionsFetching(false)
    },
    onError: () => {
      setSolutionsData({ rows: [] })
      setIsSolutionsFetching(false)
    }
  })
}, [selectedStage, stepsData])

  const changeTabActions = (value: string): void => {
    setTabValue(value)
    setSearchTerm('')
  }

  const solutionsRefetch = (): void => {
    if (!selectedStage) return

    setIsSolutionsFetching(true)

    const request: GetFormat = {
      url: `/v1/hub/idea-evaluation-step/step/${selectedStage}/summary`
    }

    fetchSolutions(request, {
      onSuccess: (data) => {
        const actualData = data?.data || data
        setSolutionsData(actualData?.rows ? actualData : { rows: actualData })
        setEvaluationsSummaryData(actualData)
        setIsSolutionsFetching(false)
      }
    })
  }

  const handleApproveSolution = (id: string): void => {
    const request: PostFormat = {
      url: `/v1/hub/idea-evaluation-step/${id}/approve`
    }

    showDoubleActionPopUp({
      title: labels.approveConfirmationModalTitle,
      message: labels.approveConfirmationModal,
      buttons: [{ text: 'Sim' }, { text: 'Não' }],
      action: () =>
        approveSolution(request, {
          onSuccess: () => {
            showToast('success', labels.successApprove)
            solutionsRefetch()
          }
        })
    })
  }

  const handleArchiveSolution = (id: string): void => {
    const request: PostFormat = {
      url: `/v1/hub/idea-evaluation-step/${id}/reject`
    }

    showDoubleActionPopUp({
      title: labels.archiveConfirmationModalTitle,
      message: labels.archiveConfirmationModal,
      buttons: [{ text: 'Sim' }, { text: 'Não' }],
      action: () =>
        archiveSolution(request, {
          onSuccess: () => {
            showToast('success', labels.successArchive)
            solutionsRefetch()
          }
        })
    })
  }

  const handleExportPdf = () => {
    notifyExport()
    setDownloadingPdf(true)

    downloadFile(
      {
        url: `/v1/hub/company/challenge/${challengeId}/report/analytical/qa/pdf`,
        httpConfig: { responseType: 'blob' }
      },
      {
        onSuccess: (res) => {
          triggerFileDownloadOnClientSide(res.data, 'relatorio-solucoes-pdf.zip')
          setDownloadingPdf(false)
        },
        onError: () => {
          showToast('error', 'Erro ao baixar PDF')
          setDownloadingPdf(false)
        }
      }
    )
  }

  const handleExportSolutionsExcel = () => {
    notifyExport()

    downloadFile(
      {
        url: `/v1/hub/company/challenge/${challengeId}/report/analytical/qa`,
        httpConfig: { responseType: 'blob' }
      },
      {
        onSuccess: (res) =>
          triggerFileDownloadOnClientSide(res.data, 'relatorio-solucoes-zing.xlsx')
      }
    )
  }

  const handleExportEvaluationExcel = () => {
    notifyExport()

    downloadFile(
      {
        url: `/v1/hub/company/challenge/${challengeId}/report/detailed`,
        httpConfig: { responseType: 'blob' }
      },
      {
        onSuccess: (res) =>
          triggerFileDownloadOnClientSide(res.data, 'zing-modelo-atribuicao-avaliadores.xlsx'),
        onError: () => showToast('error', 'Impossível exportar.')
      }
    )
  }

  const handleExportTemplateDownload = () => {
    notifyExport()

    downloadFile(
      {
        url: `/v1/hub/company/challenge/${challengeId}/template/excel/download`,
        httpConfig: { responseType: 'blob' }
      },
      {
        onSuccess: (res) => {
          triggerFileDownloadOnClientSide(res.data, 'modelo-importacao.xlsx')
        },
        onError: () => {
          showToast('error', 'Erro ao baixar o modelo de importação.')
        }
      }
    )
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) return

    try {
      const fileContent = await fileToBase64(file)

      const payload = {
        id: challengeId,
        data: {
          content: fileContent,
          mimetype: file.type,
          name: file.name,
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }

      const request: PostFormat = {
        url: `/v1/hub/company/challenge/${challengeId}/solutions/import`,
        data: payload
      }

      await api.post(request.url, request.data);
      showToast("success", "Arquivo importado com sucesso!");
      
    } catch (error) {
      showToast('error', 'Erro ao processar arquivo.')
    }
  }

  const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
      });
  };

  const exportOptions = [
    {
      label: labels.exportSolutionsPdf,
      icon: ['fas', 'file-pdf'],
      action: handleExportPdf
    },
    {
      label: labels.exportSolutionData,
      icon: ['fas', 'file-excel'],
      action: handleExportSolutionsExcel
    },
    {
      label: labels.exportEvaluationData,
      icon: ['fas', 'file-excel'],
      action: handleExportEvaluationExcel
    },
    {
      label: labels.exportSolutionsData,
      icon: ['fas', 'file-excel'],
      action: handleExportTemplateDownload
    }
  ]

  return (
    <>
      <div className="mb-6">
        <PageTitle>{labels.pageTitle}</PageTitle>
        <PageDescription>{labels.description}</PageDescription>
      </div>

      <Row className="mb-4">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: '1 1 auto', minWidth: '0' }}>
            <div style={{ position: 'relative', flex: '1 1 250px', maxWidth: '275px' }}>
              <input
                type="text"
                placeholder="Insira algumas palavras chaves"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  maxWidth: '275px',
                  boxSizing: 'border-box'
                }}
              />
              <FontAwesomeIcon
                icon={['fas', 'search']}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d'
                }}
              />
            </div>

            <button
              onClick={() => solutionsRefetch()}
              style={{
                padding: '8px 24px',
                backgroundColor: '#4a90e2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                flexShrink: 0
              }}
            >
              Buscar
            </button>

            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              style={{
                padding: '8px',
                borderRadius: '4px',
                flexShrink: 0,
                minWidth: '160px'
              }}
            >
              {stageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Área do Exportar e Importar */}
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <ExportDropdown options={exportOptions} />

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />

            <BaseButton
              variant="primary-invert"
              btnContent={
                <span>
                  <FontAwesomeIcon className="pr-2" icon={['fas', 'upload']} />
                  Importar
                </span>
              }
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
        </div>
      </Row>
              
      {isSolutionsFetching || !selectedStepData ? (
        <Loading />
      ) : isSolutionsFetching ? (
        <Loading />
      ) : isBooleanEvaluationStep(selectedStepData) ? (
        <SpecialComplianceTable
          solutions={solutionsData}
          filterValue={searchTerm}
          approveSolution={handleApproveSolution}
          archiveSolution={handleArchiveSolution}
          stepData={selectedStepData}
        />
      ) : (
        <HubCompanyChallengeSolutionsTable
          solutions={solutionsData}
          filterValue={searchTerm}
          tabStatus={tabValue}
          approveSolution={handleApproveSolution}
          archiveSolution={handleArchiveSolution}
          evaluationsSummary={evaluationsSummaryData}
          stepData={selectedStepData}
        />
      )}
    </>
  )
}